<script setup>
import { currentDayRange } from '../dateTime/historyDateTime.js'
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { ArrowPathIcon, ChevronLeftIcon, ChevronRightIcon, FunnelIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import Pts2ControllerSelector from '../components/pts2/Pts2ControllerSelector.vue'
import Pts2AlertFilters from '../components/pts2/Pts2AlertFilters.vue'
import Pts2AlertDrawer from '../components/pts2/Pts2AlertDrawer.vue'
import Pts2FilterChip from '../components/pts2/Pts2FilterChip.vue'
import { useI18n } from '../i18n.js'
import { normalizePts2Context } from '../pts2/context.js'
import { alertDescription, alertRangeIssue, getPts2Alerts, getPts2AlertDetail, getPts2AlertOptions, paginationItems } from '../pts2/alerts.js'
import { choosePts2Controller, loadPts2HistoryContext, session } from '../session.js'
import { createFacetLifecycle, emptyFacetOptions } from '../pts2/filterOptions.js'
import { useHistorySession } from '../pts2/historySession.js'
import { historyScope } from '../pts2/historyState.js'
import '../pts2/controllers.css'

const { t, locale } = useI18n()
const context = computed(() => normalizePts2Context(session.pts2Context ?? {}))
const scope = () => historyScope(session)
const pageSize = ref(25), pageNumber = ref(1), page = ref(emptyPage())
const loading = ref(true), refreshing = ref(false), switching = ref(false), denied = ref(false)
const error = ref(''), filterError = ref(''), updatedAt = ref(null)
const applied = reactive(defaultFilters()), draft = reactive({ ...applied })
const filtersOpen = ref(false), filterToggle = ref(null)
const options = reactive({ DEVICE: emptyOptions(), CODE: emptyOptions() })
const selectedId = ref(null), detail = ref(null), detailLoading = ref(false), detailError = ref('')
let listRequest, detailRequest, generation = 0, ready = false, disposed = false, optionTimer
const activeFilterGroups = computed(() => Number(applied.deviceMode !== 'ALL') + Number(applied.stateMode !== 'ALL') + Number(applied.codeMode !== 'ALL'))
const hasConditions = computed(() => activeFilterGroups.value > 0)
const activeChips = computed(() => {
  const result = []
  if (applied.deviceMode !== 'ALL') result.push({ key: 'device', label: applied.deviceMode === 'UNKNOWN' ? t('pts2AlertUnknownDevice')
    : `${t(`pts2Alert${applied.deviceType}`)} №${applied.deviceNumber}` })
  if (applied.stateMode !== 'ALL') result.push({ key: 'state', label: t(`pts2Alert${applied.stateMode}`) })
  if (applied.codeMode !== 'ALL') result.push({ key: 'code', label: applied.codeMode === 'MISSING' ? t('pts2AlertMissingCode')
    : `${t('pts2AlertCode')} ${applied.code}` })
  return result
})
const cachedStale = ref(false)
const history = useHistorySession('alerts', { applied, draft, pageNumber, pageSize, options, denied })
const unapplied = history.unapplied

const resultFrom = computed(() => page.value.total ? (page.value.page - 1) * page.value.limit + 1 : 0)
const resultTo = computed(() => page.value.total ? resultFrom.value + page.value.items.length - 1 : 0)
const pageButtons = computed(() => paginationItems(page.value.page, page.value.totalPages))
function emptyPage() { return { items: [], page: 1, limit: 25, total: 0, totalPages: 0 } }
function emptyOptions() { return emptyFacetOptions() }
function defaultFilters() {
  return { ...currentDayRange(), deviceMode: 'ALL', deviceType: null, deviceNumber: null, stateMode: 'ALL', codeMode: 'ALL', code: null }
}
const criteria = (value = applied) => ({ stationId: context.value.stationId, controllerId: context.value.activeControllerId, ...value })
function closeDrawer() { detailRequest?.abort(); selectedId.value = null; detail.value = null; detailError.value = ''; detailLoading.value = false }
let observedZone = null, optionZoneGeneration = 0
const optionScope = () => JSON.stringify([scope(), optionZoneGeneration])
function observeOptionsZone(items) {
  const zone = items.find((item) => item.stationTimeZone)?.stationTimeZone
  if (!zone) return
  if (observedZone && observedZone !== zone) {
    optionZoneGeneration++; facets.sync()
    if (filtersOpen.value && ready && !denied.value) facets.ensure()
  }
  observedZone = zone
}
const facets = createFacetLifecycle({ states: options, getCriteria: () => criteria(), getScope: optionScope,
  isValid: (value) => ready && !denied.value && !switching.value && Number(value.stationId) === Number(session.platformContext?.activeStationId) && !alertRangeIssue(value.from, value.to), load: getPts2AlertOptions,
  onDenied: loseAccess,
})
function clearOptions() { clearTimeout(optionTimer); facets.clear() }
function invalidate() {
  generation++; listRequest?.abort(); clearOptions(); closeDrawer(); page.value = emptyPage(); pageNumber.value = 1
  updatedAt.value = null; loading.value = false; refreshing.value = false; error.value = ''; filterError.value = ''; denied.value = false
}
function loseAccess() {
  history.deny(); cachedStale.value = false; invalidate(); denied.value = true; error.value = 'pts2AlertDenied' }
function message(cause, part) {
  if ([401, 403, 404].includes(cause?.status)) return 'pts2AlertDenied'
  if (part === 'detail') return 'pts2AlertDetailError'
  if (part === 'options') return 'pts2AlertOptionsError'
  return cause?.status === 503 ? 'pts2AlertTemporary' : 'pts2AlertListError'
}
async function initialize() {
  ready = false; loading.value = true; error.value = ''
  try {
    const value = await loadPts2HistoryContext({ isCurrent: () => !disposed })
    if (!value || disposed) return
    if (!context.value.activeControllerId) { history.deny(); loading.value = false; return }
    const retained = history.restore()
    ready = true
    if (filtersOpen.value) facets.ensure()
    if (retained?.page) {
      page.value = retained.page; updatedAt.value = new Date(retained.fetchedAt).toISOString()
      cachedStale.value = !retained.fresh; loading.value = false
      if (!retained.fresh) await refresh()
    } else await refresh({ initial: true })
  } catch (cause) {
    if (disposed) return
    if ([401, 403, 404].includes(cause?.status)) loseAccess()
    else { history.deny(); error.value = message(cause); loading.value = false }
  }
}

async function refresh({ resetPage = false, initial = false } = {}) {
  if (!context.value.stationId || !context.value.activeControllerId || denied.value) return
  if (resetPage) pageNumber.value = 1
  if (!history.matchesQuery()) { page.value = emptyPage(); cachedStale.value = false }
  const descriptor = history.query()
  listRequest?.abort(); const request = new AbortController(); listRequest = request
  const token = ++generation, requestScope = scope()
  loading.value = initial || page.value.items.length === 0; refreshing.value = true; error.value = ''
  try {
    const value = await getPts2Alerts(criteria(), { page: pageNumber.value, limit: pageSize.value, signal: request.signal })
    if (request.signal.aborted || token !== generation || requestScope !== scope() || disposed) return
    observeOptionsZone(value.items)
    page.value = value; pageNumber.value = value.page; updatedAt.value = new Date().toISOString()
    history.saveResult({ ...descriptor, pageNumber: value.page }, value); cachedStale.value = false
  } catch (cause) {
    if (cause?.name === 'AbortError' || token !== generation || requestScope !== scope() || disposed) return
    if ([401, 403, 404].includes(cause?.status)) loseAccess()
    else error.value = message(cause)
  } finally { if (token === generation && !disposed) { loading.value = false; refreshing.value = false } }
}
function loadOption(facet, cursor, force = false) {
  if (!ready || denied.value || switching.value) return
  return facets.load(facet, cursor === undefined ? options[facet].requestedCursor : cursor, { force })
}
function loadOptions(force = false) { if (ready && !denied.value && (filtersOpen.value || force)) return force ? facets.refresh() : facets.ensure() }
async function selectController(id) {
  switching.value = true; invalidate()
  try {
    const value = await choosePts2Controller(id)
    if (!value || disposed) return
    Object.assign(applied, defaultFilters()); Object.assign(draft, applied)
    loadOptions(); await refresh({ resetPage: true, initial: true })
  } catch (cause) { if ([401, 403, 404].includes(cause?.status)) loseAccess(); else error.value = message(cause) }
  finally { switching.value = false; if (!denied.value) loadOptions() }
}
function closeFilters() {
  filtersOpen.value = false
  nextTick(() => filterToggle.value?.focus({ preventScroll: true }))
}
async function applyFilters({ closePanel = true } = {}) {
  const issue = alertRangeIssue(draft.from, draft.to)
  if (issue) { filterError.value = `pts2Alert${{ REQUIRED: 'Required', ORDER: 'Order', TOO_LONG: 'TooLong' }[issue]}`; return }
  filterError.value = ''; Object.assign(applied, draft); closeDrawer(); page.value = emptyPage()
  if (closePanel) closeFilters()
  await Promise.all([refresh({ resetPage: true }), facets.ensure()])
}
async function clearFilters() {
  Object.assign(draft, { deviceMode: 'ALL', deviceType: null, deviceNumber: null, stateMode: 'ALL', codeMode: 'ALL', code: null })
  filterError.value = ''; filtersOpen.value = true
}
async function removeChip(key) {
  if (key === 'device') Object.assign(draft, { deviceMode: 'ALL', deviceType: null, deviceNumber: null })
  else if (key === 'state') draft.stateMode = 'ALL'
  else if (key === 'code') Object.assign(draft, { codeMode: 'ALL', code: null })
  filterError.value = ''; filtersOpen.value = true
}
async function goToPage(value) { pageNumber.value = value; closeDrawer(); await refresh() }
async function changePageSize(value) { pageSize.value = Number(value); closeDrawer(); page.value = emptyPage(); await refresh({ resetPage: true }) }
async function openAlert(item) { selectedId.value = item.id; await loadDetail() }
async function loadDetail() {
  const id = selectedId.value
  if (!id) return
  detailRequest?.abort(); const request = new AbortController(); detailRequest = request
  const requestScope = scope(); detail.value = null; detailError.value = ''; detailLoading.value = true
  try {
    const value = await getPts2AlertDetail(context.value.stationId, context.value.activeControllerId, id, { signal: request.signal })
    if (!request.signal.aborted && request === detailRequest && selectedId.value === id && requestScope === scope() && !disposed) detail.value = value
  } catch (cause) {
    if (cause?.name === 'AbortError' || request !== detailRequest || selectedId.value !== id || requestScope !== scope() || disposed) return
    if ([401, 403, 404].includes(cause?.status)) loseAccess()
    else detailError.value = message(cause, 'detail')
  } finally { if (request === detailRequest && !request.signal.aborted && !disposed) detailLoading.value = false }
}
function date(value, zone, exclusiveEnd = false) {
  if (!value) return '—'
  try { return new Intl.DateTimeFormat(locale.value === 'uk' ? 'uk-UA' : locale.value === 'ru' ? 'ru-RU' : 'en-GB', { dateStyle: 'short', timeStyle: 'medium', timeZone: zone }).format(new Date(Date.parse(value) - (exclusiveEnd ? 1000 : 0))) }
  catch { return value }
}
function device(item) { return item.deviceQuality !== 'KNOWN' ? t('pts2AlertUnknownDevice') : `${t(`pts2Alert${item.deviceType}`)}${item.deviceType === 'PTS' && item.deviceNumber === 0 ? '' : ` №${item.deviceNumber}`}` }
function eventTime(item) { return item.eventAt ? date(item.eventAt, item.stationTimeZone) : item.eventTimeLocal?.replace('T', ', ') ?? '—' }
watch(scope, () => {
  history.resetOwner(); cachedStale.value = false; filtersOpen.value = false
  observedZone = null
  invalidate(); Object.assign(applied, defaultFilters()); Object.assign(draft, applied)
  if (ready && !switching.value && context.value.stationId && context.value.activeControllerId && session.user) { loadOptions(); refresh({ resetPage: true, initial: true }) }
}, { flush: 'sync' })
watch(applied, () => {
  clearTimeout(optionTimer); facets.sync()
  if (ready && !switching.value && !denied.value && filtersOpen.value) optionTimer = setTimeout(() => loadOptions(), 150)
}, { deep: true })
watch(draft, () => { filterError.value = '' }, { deep: true })
watch(filtersOpen, (open) => { if (open) loadOptions() })
onMounted(initialize)
onBeforeUnmount(() => { history.capture(); disposed = true; ready = false; invalidate(); facets.dispose() })
</script>

<template>
  <section class="pc-page pts2-transactions-page pts2-alerts-page">
    <header class="pts2-transactions-heading"><div><h1>{{ t('pts2AlertTitle') }}</h1><p>{{ t('pts2AlertHint') }}</p></div>
      <span v-if="updatedAt">{{ t('pts2AlertUpdated', { time: new Intl.DateTimeFormat(locale, { timeStyle: 'medium' }).format(new Date(updatedAt)) }) }}</span>
    </header>
    <div class="pts2-transactions-context pts2-alert-context">
      <Pts2ControllerSelector :controllers="context.controllers" :active-controller-id="context.activeControllerId" :disabled="loading || switching || denied"
        :label="t('pts2AlertController')" :accessible-label="`${t('pts2AlertController')} PTS-2`" :placeholder="t('pts2AlertChooseController')" :unavailable-label="t('pts2AlertControllersUnavailable')" @select="selectController" />
      <button class="pc-secondary" type="button" :disabled="switching || !context.activeControllerId || denied" @click="refresh(); loadOptions(true)"><ArrowPathIcon :class="{ spinning: refreshing }" aria-hidden="true" />{{ t(refreshing ? 'pts2AlertRefreshing' : 'pts2AlertRefresh') }}</button>
    </div>
    <p v-if="unapplied" class="notice" role="status">{{ t('pts2UiHistoryUnappliedChanges') }}</p>
    <p v-if="cachedStale" class="notice" role="status">{{ t('pts2UiHistoryRetainedStale') }}</p>
    <section v-if="!loading && !error && !context.controllers.length" class="pts2-empty-state"><strong>{{ t('pts2AlertNoControllers') }}</strong></section>
    <section v-else-if="!loading && !error && !context.activeControllerId" class="pts2-empty-state"><strong>{{ t('pts2AlertChooseController') }}</strong><p>{{ t('pts2AlertSourceHint') }}</p></section>
    <section v-else class="pts2-transaction-workspace pts2-alert-workspace" :class="{ 'filters-collapsed': !filtersOpen, 'has-filter-chips': activeChips.length > 0 }">
      <div class="pts2-delivery-toolbar pts2-alert-toolbar">
        <span>{{ date(applied.from) }} — {{ date(applied.to, undefined, true) }}</span>
        <button ref="filterToggle" class="pts2-filter-toggle pc-secondary" type="button" :aria-expanded="filtersOpen" aria-controls="pts2-alert-filter-panel" @click="filtersOpen = !filtersOpen">
          <FunnelIcon aria-hidden="true" />{{ t('pts2AlertFilterToggle') }}<span v-if="activeFilterGroups">{{ activeFilterGroups }}</span>
        </button>
      </div>
      <section v-if="filtersOpen" id="pts2-alert-filter-panel"><header><h2>{{ t('pts2AlertFilters') }}</h2><button type="button" :aria-label="t('pts2AlertCloseFilters')" @click="closeFilters"><XMarkIcon aria-hidden="true" /></button></header>
        <Pts2AlertFilters :model-value="draft" :options="options" :disabled="switching || denied" :range-error="filterError ? t(filterError) : ''" @update:model-value="Object.assign(draft, $event)" @apply="applyFilters" @clear="clearFilters" @close="closeFilters" @options="loadOption" @select="facets.select" />
      </section>
      <div v-if="activeChips.length" class="pts2-active-filter-chips" aria-live="polite">
        <Pts2FilterChip v-for="chip in activeChips" :key="chip.key" appearance="neutral" :label="chip.label" :remove-label="t('pts2AlertRemoveFilter', { label: chip.label })" @remove="removeChip(chip.key)" />
        <button v-if="!filtersOpen" type="button" :disabled="switching || denied" @click="clearFilters">{{ t('pts2AlertClear') }}</button>
      </div>
      <section class="pts2-transaction-table-card pts2-alert-table-card"><header><h2>{{ t('pts2AlertJournal') }}</h2><span v-if="!loading">{{ t('pts2AlertTotal', { count: page.total }) }}</span></header>
        <p v-if="loading" class="pts2-drawer-state" role="status">{{ t('pts2AlertLoading') }}</p>
        <div v-else-if="error && !page.items.length" class="pts2-empty-state" role="alert"><strong>{{ t(error) }}</strong><button v-if="!denied" class="pc-secondary" type="button" @click="ready && context.activeControllerId ? refresh() : initialize()">{{ t('pts2AlertRetry') }}</button></div>
        <template v-else-if="page.items.length">
          <div v-if="error" class="pts2-alert-background-error" role="alert"><span>{{ t('pts2AlertBackgroundError') }} {{ t(error) }}</span><button class="pc-secondary" type="button" @click="refresh()">{{ t('pts2AlertRetry') }}</button></div>
          <div class="pts2-table-scroll"><table><thead><tr><th>{{ t('pts2AlertEventTime') }}</th><th>{{ t('pts2AlertDevice') }}</th><th>{{ t('pts2AlertState') }}</th><th>{{ t('pts2AlertCode') }}</th><th>{{ t('pts2AlertDescription') }}</th><th :aria-label="t('pts2AlertDetails')"></th></tr></thead>
            <tbody><tr v-for="item in page.items" :key="item.id" :class="{ selected: selectedId === item.id }" tabindex="0" @click="openAlert(item)" @keydown.enter.prevent="openAlert(item)" @keydown.space.prevent="openAlert(item)">
              <td :data-label="t('pts2AlertEventTime')">{{ eventTime(item) }}<small v-if="item.eventTimeQuality !== 'KNOWN'" class="pts2-alert-time-quality">{{ t(`pts2AlertTime${item.eventTimeQuality}`) }}</small></td>
              <td :data-label="t('pts2AlertDevice')">{{ device(item) }}</td><td :data-label="t('pts2AlertState')">{{ t(`pts2Alert${item.eventState}`) }}</td><td :data-label="t('pts2AlertCode')">{{ item.code ?? '—' }}</td><td :data-label="t('pts2AlertDescription')">{{ alertDescription(item, locale) }}</td>
              <td><button class="pts2-row-detail" type="button" :aria-label="t('pts2AlertDetailAction', { time: eventTime(item) })" @click.stop="openAlert(item)"><ChevronRightIcon aria-hidden="true" /></button></td>
            </tr></tbody></table></div>
        </template>
        <div v-else class="pts2-empty-state"><strong>{{ t(hasConditions ? 'pts2AlertFilterEmpty' : 'pts2AlertEmpty') }}</strong><button v-if="hasConditions" class="pc-secondary" type="button" @click="clearFilters">{{ t('pts2AlertClear') }}</button></div>
        <footer v-if="page.total" class="pts2-transaction-pagination"><label>{{ t('pts2AlertRows') }}<select :value="pageSize" @change="changePageSize($event.target.value)"><option :value="25">25</option><option :value="50">50</option><option :value="100">100</option></select></label>
          <span>{{ t('pts2AlertRange', { from: resultFrom, to: resultTo, total: page.total }) }}</span><nav :aria-label="t('pts2AlertPagination')"><button type="button" :aria-label="t('pts2AlertPrevious')" :disabled="page.page <= 1 || refreshing" @click="goToPage(page.page - 1)"><ChevronLeftIcon aria-hidden="true" /></button>
            <template v-for="button in pageButtons" :key="button"><span v-if="typeof button === 'string'">…</span><button v-else type="button" :class="{ active: button === page.page }" :aria-current="button === page.page ? 'page' : undefined" :disabled="refreshing" @click="goToPage(button)">{{ button }}</button></template>
            <button type="button" :aria-label="t('pts2AlertNext')" :disabled="page.page >= page.totalPages || refreshing" @click="goToPage(page.page + 1)"><ChevronRightIcon aria-hidden="true" /></button></nav>
        </footer>
      </section>
    </section>
    <Pts2AlertDrawer v-if="selectedId" :detail="detail" :loading="detailLoading" :error="detailError ? t(detailError) : ''" @close="closeDrawer" @retry="loadDetail" />
  </section>
</template>
